import { getAuthToken } from '../../../../services/auth/tokenStorage';
import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { letterToLovedOnesService } from '../../../../services/letterToLovedOnesInfo';
import { API_URL } from '../../../../config';

import { createLogger } from '../../../../utils/logger';
import { ensureAiConsent } from '../../../../services/ai/aiConsent';
const logger = createLogger('LetterToLovedOnesInput');

type LetterToLovedOnesInputScreenRouteProp = RouteProp<RootStackParamList, 'LetterToLovedOnesInput'>;

export const LetterToLovedOnesInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<LetterToLovedOnesInputScreenRouteProp>();

  const { letterToLovedOnesData, isEditing } = route.params || {};

  const [letterContent, setLetterContent] = useState(
    letterToLovedOnesData?.letterContent || ''
  );
  const [isLoading, setIsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!letterContent.trim()) {
      Alert.alert('Error', 'Please enter a letter to your loved ones');
      return;
    }

    try {
      setSaving(true);

      const letterUpdateData = {
        letterContent: letterContent.trim(),
      };

      let savedLetter;

      if (isEditing && letterToLovedOnesData?.id) {
        // Update existing record
        savedLetter = await letterToLovedOnesService.updateLetterToLovedOnes(
          letterToLovedOnesData.id,
          letterUpdateData
        );
        // Handle wrapped response format { success, data, message }
        const updatedData = savedLetter?.data || savedLetter;
        logger.debug('Letter to loved ones updated successfully with ID:', updatedData.id);

        // Navigate to lock/unlock screen to allow re-selection of lock/unlock
        navigation.navigate('LetterToLovedOnesLockUnlock', { letterToLovedOnesId: updatedData.id });
      } else {
        // Create new record
        savedLetter = await letterToLovedOnesService.createLetterToLovedOnes(letterUpdateData);
        // Handle wrapped response format { success, data, message }
        const createdData = savedLetter?.data || savedLetter;
        logger.debug('Letter to loved ones saved successfully with ID:', createdData.id);

        // Navigate to lock/unlock screen with the ID
        navigation.navigate('LetterToLovedOnesLockUnlock', { letterToLovedOnesId: createdData.id });
      }
    } catch (error) {
      logger.error('Error saving letter to loved ones:', error);
      Alert.alert('Error', 'Failed to save letter to loved ones. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAIAssistance = async (mode: 'write' | 'improve') => {
    if (!(await ensureAiConsent())) return;
    try {
      if (mode === 'improve' && !letterContent.trim()) {
        Alert.alert('Error', 'Please write some content first before improving.');
        return;
      }

      setIsLoading(true);
      logger.debug('Making AI request with mode:', mode);

      const token = await getAuthToken();
      if (!token) {
        Alert.alert('Error', 'Please log in to use AI assistance.');
        return;
      }

      const response = await fetch(`${API_URL}/api/ai/generate/letter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          mode,
          content: mode === 'improve' ? letterContent : undefined,
        }),
      });

      const data = await response.json();
      logger.debug('API response received:', data);

      if (data.success && data.content) {
        setLetterContent(data.content);
      } else {
        Alert.alert('Error', data.error || 'No response received from AI. Please try again.');
      }
    } catch (error: any) {
      logger.error('AI assistance error:', error);
      Alert.alert('Error', 'Failed to get AI assistance. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>
          Write a letter to your loved ones
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView}>
          <View style={styles.aiButtonsContainer}>
            <TouchableOpacity
              style={[styles.aiButton, { backgroundColor: '#29BFC0' }]}
              onPress={() => handleAIAssistance('write')}
              disabled={isLoading}
            >
              {isLoading ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.aiButtonText}>Get AI Help Writing</Text>
              )}
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.aiButton, { backgroundColor: '#FF9F64' }]}
              onPress={() => handleAIAssistance('improve')}
              disabled={isLoading || !letterContent}
            >
              {isLoading ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.aiButtonText}>Improve with AI</Text>
              )}
            </TouchableOpacity>
          </View>

          <Text style={styles.label}>Your Letter:</Text>
          <TextInput
            style={styles.letterInput}
            placeholder="Write your letter here..."
            value={letterContent}
            onChangeText={setLetterContent}
            multiline
            textAlignVertical="top"
          />

          <TouchableOpacity
            style={[styles.saveButton, saving && { opacity: 0.7 }]}
            onPress={handleSave}
            disabled={saving}
          >
            {saving ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <Text style={styles.saveButtonText}>{isEditing ? 'Update' : 'Save'}</Text>
            )}
          </TouchableOpacity>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
   backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  aiButtonsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: ms(20),
    gap: ms(10),
  },
  aiButton: {
    flex: 1,
    padding: ms(12),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  aiButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  label: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(10),
  },
  letterInput: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    padding: ms(15),
    marginBottom: ms(30),
    minHeight: s(300),
    textAlignVertical: 'top',
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  saveButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
    marginBottom: ms(30),
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  bottomPadding: {
    height: s(120),
  },
});

export default LetterToLovedOnesInputScreen;
